import {Paths} from "../../util/url.js";
import {getToken, httpGet, httpPost, httpPut} from "./http.js";
import {Store} from "../../store.js";
import {showErrorPopup} from "../../util/errors.js";
import {httpPostFormData} from "../../util/http.js";
import {ChatView} from "./views/chat-view.js";
import {last, random, uniqueId} from "lodash-es";
import {bytesToUnit} from "../../util/number.js";
import {MESSAGE_ORIGIN_CUSTOMER} from "./types.js";
import {formatISO} from "date-fns";

const POLL_INTERVAL = 5000;

export class Chat {

    constructor({paths, ...props}) {
        this.paths = Paths.factory(paths);
        this.props = props;
        this.store = new Store({
            messages: [],
            lastFetchId: null,
            state: {
                buttonLabel: props.buttonLabel || '',
                showResponseInput: false,
                alwaysShowResponseInput: false,
            },
        });

        this.store.addEventListener(this, this.storeEvents());
        this.$chatContainer = jQuery('<div>', {class: 'pk-chat-container'});
        this.$chatContainer.appendTo(props.parent || jQuery('body'));
        this.fetchUnreadMessagesCount().then();
        this.showChatView();
    }

    storeEvents() {
        return {
            'change:messages': () => {
                if (this.getState().isChatVisible) {
                    return this.markUnreadMessagesAsRead();
                }
            },
            'change:state:isChatVisible': async () => {
                if (this.getState().isChatVisible) {
                    await this.fetchMessages();
                    this.fetchUnreadMessagesCount().then();
                    this.scheduleFetchMessages();
                } else {
                    this.clearScheduledFetchMessages();
                }
            }
        };
    }

    setState(props) {
        this.store.merge('state', props);
    }

    getState() {
        return this.store.get('state', {});
    }

    showChatView() {
        this.chatView = new ChatView({
            $el: this.$chatContainer,
            store: this.store,
            chat: this,
        });
        this.chatView.render();
    }

    toggleChatWindow(show) {
        this.store.merge('state', {
            isChatVisible: !!show,
        });
    }

    isActiveWindow() {
        return this.store.get('state')?.isChatVisible === true;
    }

    clearScheduledFetchMessages() {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    scheduleFetchMessages(time = POLL_INTERVAL) {
        this.clearScheduledFetchMessages();
        this.pollTimer = setTimeout(async () => {
            await this.fetchMessages();
            this.scheduleFetchMessages(POLL_INTERVAL);
        }, time);
    }

    async fetchMessages() {
        const state = this.getState();
        if (state.isMessagesRequested) {
            return;
        }

        this.setState({isMessagesRequested: true});
        try {
            const data = await httpGet(this.paths.get('rest_chat', {
                lastId: state.lastFetchId,
                namespace: this.props.namespace,
            }), {namespace: this.props.namespace});
            this.appendMessages(data);
            const lastMessage = last(data);
            this.setState({
                lastFetchId: lastMessage ? lastMessage.id : state.lastFetchId,
            });

            if (lastMessage) {
                this.setState({
                    showResponseInput: !!lastMessage.is_expecting_response,
                    alwaysShowResponseInput: !!lastMessage.is_employee_connected || !!lastMessage.always_expect_response,
                });
            }
        } catch (e) {
        }
        this.setState({isMessagesRequested: false});
    }

    appendMessages(messages, minTime = 500, maxTime = 1200)
    {
        if (messages.length > 1 && this.store.get('messages', []).length > 0) {
            this.doAppendMessages(messages, minTime, maxTime);
        } else {
            this.store.merge('messages', messages);
            this.setState({isAwaitingResponse: false});
        }
    }

    doAppendMessages(messages, minTime = 500, maxTime = 1200)
    {
        this.setState({isAwaitingResponse: true});
        let time = 0;
        for (let message of messages) {
            time += random(minTime, maxTime);
            setTimeout(() => {
                this.store.merge('messages', [message]);
                if (message.is_expecting_response) {
                    this.setState({showResponseInput: true});
                }
            }, time);
        }
        setTimeout(() => {
            this.setState({isAwaitingResponse: false});
        }, time);
    }

    async fetchUnreadMessagesCount() {
        try {
            const data = await httpGet(this.paths.get('rest_chat_unread_messages_count'), {namespace: this.props.namespace});
            this.setState({
                unreadMessagesCount: Number(data.unread_messages) || 0,
            });
        } catch (e) {
        }
    }

    getUnreadMessagesCount() {
        return this.getState().unreadMessagesCount || 0;
    }

    async uploadFile(file) {
        if (file.size > this.props.maxFileSize) {
            alertify.error(`Maksymalny rozmiar pliku to ${bytesToUnit(this.props.maxFileSize)}`);
            return;
        }

        const name = String(file.name);
        const ext = name.substring(name.lastIndexOf('.') + 1);
        if (this.props.permittedFileTypes.indexOf(ext) === -1) {
            alertify.error(`Dopuszczalne typy plików to ${this.props.permittedFileTypes.join(', ')}`);
            return;
        }

        try {
            const data = new FormData();
            data.append('file', file);
            return await httpPostFormData(this.paths.get('rest_chat_messages_files_post'), data, {
                headers: {
                    Authorization: `Bearer ${getToken(this.props.namespace)}`,
                    'X-Pk-Chat-Namespace': this.props.namespace || '',
                },
            });
        } catch (e) {
            showErrorPopup(e);
        }
    }

    getMessages() {
        return this.store.get('messages', []);
    }

    getLastMessage() {
        const messages = this.getMessages();
        return messages[messages.length - 1];
    }

    async postMessage(content, attachments = []) {
        const tempId = uniqueId('message_');
        this.clearScheduledFetchMessages();
        try {
            const lastMessage = this.getLastMessage();
            this.store.merge('messages', [{
                origin: MESSAGE_ORIGIN_CUSTOMER,
                id: tempId,
                chat_id: lastMessage ? lastMessage.chat_id : null,
                content,
                attachments,
                date: formatISO(new Date()),
                isBeingSend: true,
            }]);

            if (!this.getState().alwaysShowResponseInput) {
                this.setState({showResponseInput: false});
            }
            const {isAwaitingResponse, messages} = await httpPost(this.paths.get('rest_chat_messages_post'), {
                content,
                attachments: attachments ? attachments.map(m => m.id || String(m)) : [],
            }, {namespace: this.props.namespace});

            const message = messages[0] || {};
            this.store.set('messages', [
                ...this.getMessages().filter(m => m.id !== tempId && m.id !== message.id),
                message,
            ]);

            if (isAwaitingResponse === true) {
                this.scheduleFetchMessages();
                this.setState({
                    isAwaitingResponse: true,
                    lastFetchId: messages[messages.length - 1].id,
                });
                this.doAppendMessages(messages.slice(1));
            }
        } catch (e) {
            this.store.set('messages', this.getMessages().filter(m => m.id !== tempId));
            showErrorPopup(e);
        }
        this.scheduleFetchMessages();
    }

    async processMessage(message) {
        this.clearScheduledFetchMessages();
        try {
            this.setState({isAwaitingResponse: true});
            const {isAwaitingResponse, messages} = await httpPost(this.paths.get('rest_chat_messages_process', {
                messageId: message.id,
            }), null, {namespace: this.props.namespace});

            this.setState({isAwaitingResponse});
            this.doAppendMessages(messages);
        } catch (e) {
            showErrorPopup(e);
        }
        this.scheduleFetchMessages();
    }

    async processLastUserMessage() {
        const userMessages = this.getMessages().filter(m => m.origin === MESSAGE_ORIGIN_CUSTOMER);
        const lastUserMessage = userMessages[userMessages.length - 1];
        if (lastUserMessage) {
            return this.processMessage(lastUserMessage);
        }
    }

    async markUnreadMessagesAsRead() {
        try {
            const ids = this.getMessages().filter(m => m.origin !== MESSAGE_ORIGIN_CUSTOMER && !m.is_read);
            if (ids.length > 0) {
                await httpPut(this.paths.get('rest_chat_mark_messages_as_read'), {ids}, {namespace: this.props.namespace});
                this.setState({unreadMessagesCount: 0});
            }
        } catch (e) {}
    }

}
